import React, { useEffect, useState, useRef, useCallback } from "react";
import DashboardHeader from "../../components/auth/DashboardHeader";
import Sidebar from "../../components/auth/Sidebar";
import { Loader } from "../../components/Loader";
import API from "../../helper/AxiosInterceptor";
import { toast } from "react-toastify";
import { formatDate, trimAddress, copyToClipboard } from "../../helper/helper";
import Swal from "sweetalert2";
import { useAppSelection } from "../../context/AppSelectionContext";

const UserWithdrawals = () => {
    const { selectedApp, setSelectedApp, apps, fetchApps } = useAppSelection();
    const [withdrawals, setWithdrawals] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [walletBalances, setWalletBalances] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const searchTimerRef = useRef(null);

    // Debounce search input
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, 300);
    }, []);

    // Fetch withdrawals
    const fetchWithdrawals = async () => {
        if (!selectedApp) return;

        try {
            setIsLoading(true);
            const resp = await API.get(
                `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/list`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params: {
                        appId: selectedApp,
                        status: selectedStatus || undefined,
                        search: debouncedSearch || undefined,
                        pageNo: currentPage,
                        limitVal: 20,
                    },
                }
            );
            setWithdrawals(resp?.data?.data || []);
            setCurrentPage(resp?.data?.currentPage || 1);
            setTotalPages(resp?.data?.totalPages || 1);
        } catch (error) {
            console.error("Error fetching withdrawals:", error);
            toast.error("Failed to fetch withdrawals");
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch wallet balances
    const fetchWalletBalances = async () => {
        if (!selectedApp) return;
        try {
            const resp = await API.get(
                `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/balance`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params: { appId: selectedApp }
                }
            );
            if (resp.data.success) {
                setWalletBalances(resp.data.data.balances || []);
            }
        } catch (error) {
            console.error("Error fetching wallet balances:", error);
        }
    };

    useEffect(() => {
        fetchApps();
    }, []);

    useEffect(() => {
        if (selectedApp) {
            fetchWithdrawals();
        }
    }, [selectedApp, selectedStatus, currentPage, debouncedSearch]);

    useEffect(() => {
        if (selectedApp) {
            setWalletBalances([]); // Clear previous balances to avoid stale data
            fetchWalletBalances();
        }
    }, [selectedApp]);



    const handlePageChange = (pageNo) => {
        if (pageNo > 0 && pageNo <= totalPages) {
            setCurrentPage(pageNo);
        }
    };

    const handleApprove = async (withdrawalId) => {
        const result = await Swal.fire({
            title: "Approve Withdrawal?",
            text: "This will initiate the transfer to the user's wallet.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#28a745",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Approve",
        });

        if (result.isConfirmed) {
            try {
                setActionLoading(withdrawalId);
                await API.post(
                    `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/approve`,
                    { withdrawalId },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                toast.success("Withdrawal approved!");
                fetchWithdrawals();
            } catch (error) {
                console.error("Error approving withdrawal:", error);
                toast.error(error?.response?.data?.message || "Failed to approve withdrawal");
            } finally {
                setActionLoading(null);
            }
        }
    };

    const handleDecline = async (withdrawalId) => {
        const { value: reason, isConfirmed } = await Swal.fire({
            title: "Decline Withdrawal",
            input: "textarea",
            inputLabel: "Reason for declining",
            inputPlaceholder: "Enter reason (required)",
            inputValidator: (value) => {
                if (!value) {
                    return "Please provide a reason";
                }
            },
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Decline",
        });

        if (isConfirmed && reason) {
            try {
                setActionLoading(withdrawalId);
                await API.post(
                    `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/decline`,
                    { withdrawalId, reason },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                toast.success("Withdrawal declined");
                fetchWithdrawals();
            } catch (error) {
                console.error("Error declining withdrawal:", error);
                toast.error(error?.response?.data?.message || "Failed to decline withdrawal");
            } finally {
                setActionLoading(null);
            }
        }
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            PENDING: { bg: "#ffc107", color: "#000" },
            AUTO_APPROVED: { bg: "#17a2b8", color: "#fff" },
            APPROVED: { bg: "#28a745", color: "#fff" },
            PROCESSING: { bg: "#007bff", color: "#fff" },
            SUCCESS: { bg: "#198754", color: "#fff" },
            FAILED: { bg: "#dc3545", color: "#fff" },
            DECLINED: { bg: "#6c757d", color: "#fff" },
        };

        const style = statusStyles[status] || { bg: "#6c757d", color: "#fff" };

        return (
            <span
                style={{
                    backgroundColor: style.bg,
                    color: style.color,
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                }}
            >
                {status}
            </span>
        );
    };

    return (
        <>
            <DashboardHeader />

            <div className="site-bg">
                <div className="dashborad-content-bg">
                    <aside className="sidebar">
                        <Sidebar />
                    </aside>
                    <div className="dashborad-warper" style={{ position: "relative" }}>
                        {isLoading && <Loader inline={true} />}
                        <div className="transactions-list">
                            <div className="dashborad-head">
                                <div className="breadcrumb-menu">
                                    <h3>User Withdrawals</h3>
                                    <p>Manage withdrawal requests from your platform users</p>
                                </div>
                                <div className="dashborad-head-right" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                    {/* App Selector */}
                                    <div className="year-select-box">
                                        <select
                                            className="form-control"
                                            value={selectedApp}
                                            onChange={(e) => {
                                                setSelectedApp(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="">Select App</option>
                                            {apps.map((app) => (
                                                <option key={app._id} value={app._id}>
                                                    {app.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Status Filter */}
                                    <div className="year-select-box">
                                        <select
                                            className="form-control"
                                            value={selectedStatus}
                                            onChange={(e) => {
                                                setSelectedStatus(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="">All Status</option>
                                            <option value="PENDING">Pending</option>
                                            <option value="AUTO_APPROVED">Auto Approved</option>
                                            <option value="APPROVED">Approved</option>
                                            <option value="PROCESSING">Processing</option>
                                            <option value="SUCCESS">Success</option>
                                            <option value="FAILED">Failed</option>
                                            <option value="DECLINED">Declined</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Search Box */}
                            <div style={{ padding: "15px 20px 0", backgroundColor: "#fff", borderRadius: "8px 8px 0 0", marginBottom: "-1px" }}>
                                <div style={{ position: "relative", maxWidth: "450px" }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by username, email, userId, amount, txHash, or reference..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        style={{
                                            padding: "10px 15px 10px 40px",
                                            borderRadius: "8px",
                                            border: "1px solid #ddd",
                                            fontSize: "14px",
                                            width: "100%",
                                            transition: "border-color 0.2s",
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = "#f0b90b"}
                                        onBlur={(e) => e.target.style.borderColor = "#ddd"}
                                    />
                                    <span style={{
                                        position: "absolute",
                                        left: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#999",
                                        fontSize: "16px",
                                        pointerEvents: "none",
                                    }}>
                                        🔍
                                    </span>
                                </div>
                            </div>

                            <div className="white-bg my-apps-content padd20">
                                {withdrawals.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "40px" }}>
                                        <p style={{ color: "#666" }}>No withdrawal requests found</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>User</th>
                                                    <th>Amount</th>
                                                    <th>Wallet Address</th>
                                                    <th>Status</th>
                                                    <th>Created At</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {withdrawals.map((withdrawal) => (
                                                    <tr key={withdrawal._id}>
                                                        <td>
                                                            <div>
                                                                <strong>{withdrawal.userName || withdrawal.userId}</strong>
                                                                {withdrawal.userEmail && (
                                                                    <div style={{ fontSize: "12px", color: "#666" }}>
                                                                        {withdrawal.userEmail}
                                                                    </div>
                                                                )}
                                                                {withdrawal.externalReference && (
                                                                    <div style={{ fontSize: "11px", color: "#999" }}>
                                                                        Ref: {withdrawal.externalReference}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <strong>{withdrawal.amount} {withdrawal.tokenSymbol}</strong>
                                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                                Chain: {withdrawal.chainId}
                                                            </div>
                                                            {withdrawal.transactionType === "FIAT" && (
                                                                <div style={{ fontSize: "11px", color: "#856404", backgroundColor: "#fff3cd", padding: "2px 6px", borderRadius: "4px", display: "inline-block", marginTop: "4px" }}>
                                                                    Fiat Request: {withdrawal.fiatAmount} {withdrawal.fiatCurrency?.toUpperCase()}
                                                                </div>
                                                            )}
                                                            <div style={{ fontSize: "12px", color: "#28a745", marginTop: "4px" }}>
                                                                Available: {(() => {
                                                                    const balance = walletBalances.find(b =>
                                                                        (b.symbol === withdrawal.tokenSymbol || b.code === withdrawal.tokenSymbol) &&
                                                                        b.chainId === withdrawal.chainId
                                                                    );
                                                                    return balance ? `${parseFloat(balance.balance).toFixed(6)} ${withdrawal.tokenSymbol}` : "Checking...";
                                                                })()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => copyToClipboard(withdrawal.walletAddress)}
                                                                title="Click to copy"
                                                            >
                                                                {trimAddress(withdrawal.walletAddress, 8, 6)}
                                                            </span>
                                                        </td>
                                                        <td>{getStatusBadge(withdrawal.status)}</td>
                                                        <td>{formatDate(withdrawal.createdAt)}</td>
                                                        <td>
                                                            {withdrawal.status === "PENDING" && (
                                                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                                                    <style>
                                                                        {`
                                                                            .btn-approve-custom {
                                                                                background-color: #28a745 !important;
                                                                                border-color: #28a745 !important;
                                                                                color: #000 !important;
                                                                            }
                                                                            .btn-approve-custom:hover {
                                                                                background-color: #218838 !important;
                                                                            }
                                                                            .btn-decline-custom {
                                                                                background-color: #dc3545 !important;
                                                                                border-color: #dc3545 !important;
                                                                                color: #000 !important;
                                                                            }
                                                                            .btn-decline-custom:hover {
                                                                                background-color: #c82333 !important;
                                                                            }
                                                                        `}
                                                                    </style>
                                                                    <button
                                                                        className="btn btn-sm btn-approve-custom"
                                                                        onClick={() => handleApprove(withdrawal._id)}
                                                                        disabled={actionLoading === withdrawal._id}
                                                                        title="Approve"
                                                                        style={{
                                                                            padding: "6px 14px",
                                                                            borderRadius: "4px",
                                                                            cursor: "pointer",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "5px",
                                                                            fontSize: "13px",
                                                                            fontWeight: "600",
                                                                            boxShadow: "0 2px 4px rgba(40, 167, 69, 0.2)"
                                                                        }}
                                                                    >
                                                                        {actionLoading === withdrawal._id ? "..." : "Approve"}
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-decline-custom"
                                                                        onClick={() => handleDecline(withdrawal._id)}
                                                                        disabled={actionLoading === withdrawal._id}
                                                                        title="Decline"
                                                                        style={{
                                                                            padding: "6px 14px",
                                                                            borderRadius: "4px",
                                                                            cursor: "pointer",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "5px",
                                                                            fontSize: "13px",
                                                                            fontWeight: "600",
                                                                            boxShadow: "0 2px 4px rgba(220, 53, 69, 0.2)"
                                                                        }}
                                                                    >
                                                                        Decline
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {withdrawal.status === "SUCCESS" && withdrawal.txHash && (
                                                                <span
                                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                                    onClick={() => copyToClipboard(withdrawal.txHash)}
                                                                    title="Click to copy tx hash"
                                                                >
                                                                    {trimAddress(withdrawal.txHash, 6, 4)}
                                                                </span>
                                                            )}
                                                            {withdrawal.status === "FAILED" && withdrawal.failureReason && (
                                                                <span style={{ color: "#dc3545", fontSize: "12px" }}>
                                                                    {withdrawal.failureReason}
                                                                </span>
                                                            )}
                                                            {withdrawal.status === "DECLINED" && withdrawal.declineReason && (
                                                                <span style={{ color: "#6c757d", fontSize: "12px" }}>
                                                                    {withdrawal.declineReason}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="pagination-box">
                                        <ul>
                                            <li>
                                                <a
                                                    href=""
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(currentPage - 1);
                                                    }}
                                                    disabled={currentPage === 1}
                                                >
                                                    <img src="/images/left-arrow.png" alt="arrow" />
                                                </a>
                                            </li>
                                            {[...Array(Math.min(totalPages, 5)).keys()].map((pageNo) => (
                                                <li
                                                    key={pageNo + 1}
                                                    className={currentPage === pageNo + 1 ? "active" : ""}
                                                >
                                                    <a
                                                        href=""
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handlePageChange(pageNo + 1);
                                                        }}
                                                    >
                                                        {pageNo + 1}
                                                    </a>
                                                </li>
                                            ))}
                                            <li>
                                                <a
                                                    href=""
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(currentPage + 1);
                                                    }}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <img src="/images/right-arrow.png" alt="arrow" />
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserWithdrawals;
